import { CalendarUtils } from './calendar-utils'

/**
 * Nearest-valid-stay resolvers for the calendar tooltip's one-tap
 * "Select {from} – {to}" action (toolkit calendar-hover-tips parity,
 * #1071 cluster). Pure functions over a CalendarUtils instance so the
 * search logic is unit-testable without a DOM.
 */

export interface StaySuggestion {
  checkin: string
  checkout: string
  nights: number
}

/** How far either side of the rejected day to look for a valid check-in
 *  (toolkit calendar-hover-tips.js SEARCH_DAYS). */
export const SEARCH_DAYS = 30

/** Add n days to a YYYY-MM-DD string, UTC-safe (string dates parse as UTC midnight). */
export function addDays(day: string, n: number): string {
  const d = new Date(day + 'T00:00:00Z')
  if (isNaN(d.getTime())) return day
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/**
 * Entry of an ascending YYYY-MM-DD list closest to `day`. A tie goes to
 * the earlier date (the shorter stay). Null for an empty list.
 */
export function nearestDay(days: string[], day: string): string | null {
  if (!Array.isArray(days) || !days.length || !day) return null
  const target = new Date(day + 'T00:00:00Z').getTime()
  if (isNaN(target)) return null
  let best: string | null = null
  let bestDist = Infinity
  for (const d of days) {
    const t = new Date(d + 'T00:00:00Z').getTime()
    if (isNaN(t)) continue
    const dist = Math.abs(t - target)
    if (dist < bestDist) {
      best = d
      bestDist = dist
    }
  }
  return best
}

/**
 * Picking a check-out that a rule rejects: the valid check-out day for the
 * selected check-in closest to the hovered/clicked day.
 */
export function resolveCheckoutSuggestion(
  utils: CalendarUtils,
  checkin: string,
  day: string,
): StaySuggestion | null {
  const checkout = nearestDay(utils.getAvailableCheckoutDays(checkin), day)
  if (!checkout) return null
  return { checkin, checkout, nights: utils.calcNights(checkin, checkout) }
}

/**
 * Picking a check-in that a rule rejects: the nearest day either side
 * (within SEARCH_DAYS) that can check in, paired with its first valid
 * check-out. At equal distance the earlier day wins.
 */
export function resolveCheckinSuggestion(
  utils: CalendarUtils,
  day: string,
): StaySuggestion | null {
  for (let dist = 1; dist <= SEARCH_DAYS; dist++) {
    for (const candidate of [addDays(day, -dist), addDays(day, dist)]) {
      if (!utils.canCheckin(candidate).status) continue
      const checkout = utils.getAvailableCheckoutDays(candidate)[0]
      if (!checkout) continue
      return { checkin: candidate, checkout, nights: utils.calcNights(candidate, checkout) }
    }
  }
  return null
}

/**
 * "Aug 30" in the page locale; the year is appended only when it differs
 * from `nowYear` (so a stay next January reads "Jan 3, 2027").
 */
export function formatSuggestionDay(iso: string, locale: string | undefined, nowYear: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  if (isNaN(d.getTime())) return iso
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' }
  if (d.getUTCFullYear() !== nowYear) opts.year = 'numeric'
  try {
    return d.toLocaleDateString(locale || undefined, opts)
  } catch {
    return d.toLocaleDateString(undefined, opts)
  }
}
